import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { bugService } from '@/database/services'

export const useBugStore = defineStore('bug', () => {
  // State
  const bugs = ref([])
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const getBugs = computed(() => bugs.value)
  const getLoading = computed(() => loading.value)
  const getError = computed(() => error.value)

  // Actions
  const fetchBugs = async (filters = {}) => {
    try {
      loading.value = true
      error.value = null
      const data = await bugService.getAll(filters)
      bugs.value = data
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching bugs:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchBugById = async (id) => {
    try {
      loading.value = true
      error.value = null
      const data = await bugService.getById(id)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching bug:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const createBug = async (bugData) => {
    try {
      loading.value = true
      error.value = null
      const data = await bugService.create(bugData)
      bugs.value.push(data)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error creating bug:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateBug = async (id, bugData) => {
    try {
      loading.value = true
      error.value = null
      const data = await bugService.update(id, bugData)
      const index = bugs.value.findIndex(b => b.id === id)
      if (index !== -1) {
        bugs.value[index] = data
      }
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error updating bug:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteBug = async (id) => {
    try {
      loading.value = true
      error.value = null
      await bugService.delete(id)
      bugs.value = bugs.value.filter(b => b.id !== id)
      return true
    } catch (err) {
      error.value = err.message
      console.error('Error deleting bug:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchBugsByProject = async (projectId) => {
    try {
      loading.value = true
      error.value = null
      const data = await bugService.getByProject(projectId)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching bugs by project:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchBugsByStatus = async (projectId, status) => {
    try {
      loading.value = true
      error.value = null
      const data = await bugService.getByStatus(projectId, status)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching bugs by status:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  const clearBugs = () => {
    bugs.value = []
  }

  // Convenience methods that match what views expect
  const getBugsAsync = async (filters = {}) => {
    return await fetchBugs(filters)
  }

  const getBug = async (id) => {
    return await fetchBugById(id)
  }

  return {
    // State
    bugs,
    loading,
    error,
    // Getters
    getBugs, // computed getter
    getLoading,
    getError,
    // Actions
    fetchBugs,
    fetchBugById,
    createBug,
    updateBug,
    deleteBug,
    fetchBugsByProject,
    fetchBugsByStatus,
    clearError,
    clearBugs,
    // Convenience methods - aliased to avoid duplicate key warnings
    fetchBugsAsync: getBugsAsync, // async method for API calls
    getBugById: getBug
  }
})
